/**
 * Feedback Form Component
 * Compact form for alert feedback with all editable fields
 */

import React, { useState, useEffect, useRef } from 'react';
import { Save, X, Activity, Loader2, Image as ImageIcon, Eye, Trash2 } from 'lucide-react';
import { Alert, AlertFeedback, FaultAccuracy, RectificationStatus } from '../types';
import dropdownOptions from '../../../../data/dropdowns.json';
import { alertsApi } from '../services/alertsApi';
import backendApi from '../../../../services/backendApi';

interface FeedbackFormProps {
  alert: Alert;
  feedback: AlertFeedback;
  onFieldChange: (field: keyof AlertFeedback, value: any) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
  isAdmin?: boolean; // Whether user is admin/superuser
  onFaultCategorySelect?: (faultCategory: string) => void; // Callback when fault category is selected
}

// Flatten all fault category options and remove duplicates
const getAllFaultCategories = (): string[] => {
  const categorySet = new Set<string>();
  Object.values(dropdownOptions).forEach(categoryArray => {
    categoryArray.forEach(category => {
      categorySet.add(category);
    });
  });
  return Array.from(categorySet).sort();
};

const resolveAlertSiteCode = (alert: Alert): string => {
  const fromAlert = (alert as any)?.site_code || (alert as any)?.SITE_CODE;
  if (typeof fromAlert === 'string' && fromAlert.trim()) return fromAlert.trim();

  const fromSession = sessionStorage.getItem('tenant');
  if (fromSession?.trim()) return fromSession.trim();

  const fromLocal = localStorage.getItem('currentSite');
  if (fromLocal?.trim()) return fromLocal.trim();

  return '';
};

export const FeedbackForm: React.FC<FeedbackFormProps> = ({
  alert,
  feedback,
  onFieldChange,
  onSave,
  onCancel,
  saving,
  isAdmin = false,
  onFaultCategorySelect
}) => {
  const [faultCategories, setFaultCategories] = useState<string[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const faultCategoryBeforeOpenRef = useRef<string | null>(null);
  const faultCategoryDropdownActiveRef = useRef<boolean>(false);

  /* State for Users (AssignedTo / ResolvedBy) */
  const [users, setUsers] = useState<any[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      setIsLoadingUsers(true);
      try {
        const response = await backendApi.getUsers();
        if (response.success && response.users) {
          setUsers(response.users);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        setIsLoadingUsers(false);
      }
    };
    fetchUsers();
  }, []);

  /* State for Fault Categories */
  useEffect(() => {
    const fetchCategories = async () => {
      const equipment = alert.EQUIPMENT || alert.itemname;
      const savedCategory = feedback.fault_category;
      const siteCode = resolveAlertSiteCode(alert);

      setLoadingCategories(true);
      try {
        let categories: string[] = [];

        // Always include live inventory categories for this site.
        const inventoryCategories = await alertsApi.fetchInventoryCategories(siteCode);

        if (equipment) {
          const response = await alertsApi.fetchFaultCategoriesByEquipment(equipment, siteCode);
          if (response.fault_categories && response.fault_categories.length > 0) {
            categories = response.fault_categories;
          } else {
}
        }

        const merged = new Set<string>([
          ...categories,
          ...inventoryCategories,
          ...getAllFaultCategories(),
        ].map((v) => (v || '').trim()).filter(Boolean));

        categories = Array.from(merged).sort((a, b) => a.localeCompare(b));

        // CRITICAL BUG FIX: Ensure the saved category is in the list
        // If it's not in the list, the <select> will show as empty even if it has a value
        if (savedCategory && !categories.includes(savedCategory)) {
categories = [...categories, savedCategory].sort();
        }

        setFaultCategories(categories);
      } catch (error) {
        console.error("Error fetching fault categories:", error);
        let categories = getAllFaultCategories();
        if (savedCategory && !categories.includes(savedCategory)) {
          categories = [...categories, savedCategory].sort();
        }
        setFaultCategories(categories);
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchCategories();
  }, [alert, alert.EQUIPMENT, alert.itemname, feedback.fault_category]);


  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        onFieldChange('uploaded_image', reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    onFieldChange('uploaded_image', null);
  };

  return (
    <>
      {fullscreenImage && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
          onClick={() => setFullscreenImage(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh] w-full flex items-center justify-center">
            <button
              className="absolute -top-12 right-0 text-white hover:text-gray-300 p-2"
              onClick={() => setFullscreenImage(null)}
            >
              <X size={32} />
            </button>
            <img
              src={fullscreenImage}
              alt="Full size preview"
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
      <div className="feedback-form-compact">
        <h4 className="form-title">
          <Activity />
          Alert Feedback
        </h4>

        <div className="form-content">
          {/* Grid 1: Basic Information */}
          <div className="form-grid-1">
            <div className="form-group">
              <label className="filter-label">Fault Accuracy</label>
              <select
                className="input"
                value={feedback.fault_accuracy || ''}
                onChange={e => onFieldChange('fault_accuracy', e.target.value || null)}
              >
                <option value="">Select...</option>
                <option value="true">Accurate</option>
                <option value="false">Inaccurate</option>
              </select>
            </div>

            <div className="form-group">
              <label className="filter-label">Fault Description *</label>
              <input
                className="input"
                placeholder="Describe fault..."
                value={feedback.fault_description || ''}
                onChange={e => onFieldChange('fault_description', e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label className="filter-label">Energy Loss</label>
              <input
                type="number"
                step="0.001"
                className="input"
                placeholder="Enter energy loss..."
                value={feedback.energy_loss ?? ''}
                onChange={e => {
                  const inputValue = e.target.value;
                  if (inputValue === '') {
                    onFieldChange('energy_loss', null);
                  } else {
                    const numValue = parseFloat(inputValue);
                    if (!isNaN(numValue) && isFinite(numValue)) {
                      onFieldChange('energy_loss', numValue);
                    }
                  }
                }}
                min="0"
              />
            </div>

            <div className="form-group">
              <label className="filter-label">Resolved At</label>
              <input
                type="datetime-local"
                className="input"
                value={feedback.resolved_at ? new Date(feedback.resolved_at).toISOString().slice(0, 16) : ''}
                onChange={e => onFieldChange('resolved_at', e.target.value ? new Date(e.target.value).toISOString() : null)}
              />
            </div>
          </div>


          {/* Grid 2: Status & Assignment */}
          <div className="form-grid-2">
            <div className="form-group">
              <label className="filter-label">Status</label>
              <select
                className="input"
                value={feedback.fault_rectification_status || ''}
                onChange={e => onFieldChange('fault_rectification_status', e.target.value || null)}
              >
                <option value="">Select...</option>
                <option value="rectified">Rectified</option>
                <option value="not rectified">Not Rectified</option>
              </select>
            </div>

            <div className="form-group">
              <label className="filter-label">Quantum of Fault #</label>
              <input
                type="number"
                className="input"
                placeholder="Enter number..."
                value={feedback.quantum_of_fault ?? feedback.no_of_faulty_strings ?? ''}
                onChange={e => {
                  const inputValue = e.target.value;
                  if (inputValue === '') {
                    onFieldChange('quantum_of_fault', null);
                    onFieldChange('no_of_faulty_strings', null);
                  } else {
                    const numValue = parseInt(inputValue, 10);
                    if (!isNaN(numValue)) {
                      onFieldChange('quantum_of_fault', numValue);
                      onFieldChange('no_of_faulty_strings', numValue);
                    }
                  }
                }}
                min="0"
              />
            </div>

            <div className="form-group">
              <label className="filter-label">Assigned To</label>
              {isLoadingUsers ? (
                <div className="input loading-text">Loading users...</div>
              ) : (
                <select
                  className="input"
                  value={feedback.assigned_to ?? ''}
                  onChange={e => {
                    const val = e.target.value;
                    onFieldChange('assigned_to', val ? parseInt(val, 10) : null);
                  }}
                >
                  <option value="">Select User...</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.full_name || user.username}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div className="form-group">
              <label className="filter-label">Resolved By</label>
              {isLoadingUsers ? (
                <div className="input loading-text">Loading users...</div>
              ) : (
                <select
                  className="input"
                  value={feedback.resolved_by ?? ''}
                  onChange={e => {
                    const val = e.target.value;
                    onFieldChange('resolved_by', val ? parseInt(val, 10) : null);
                  }}
                >
                  <option value="">Select User...</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.full_name || user.username}
                    </option>
                  ))}
                </select>
              )}

            </div>
          </div>

          {/* Grid 3: Category & Impact */}
          <div className="form-grid-3">
            <div className="form-group">
              <label className="filter-label">Fault Category</label>
              <select
                className="input"
                value={feedback.fault_category || ''}
                onMouseDown={() => {
                  // Store current value before dropdown interaction starts.
                  faultCategoryBeforeOpenRef.current = feedback.fault_category || null;
                  faultCategoryDropdownActiveRef.current = true;
                }}
                onBlur={() => {
                  const previousValue = faultCategoryBeforeOpenRef.current;
                  const currentValue = feedback.fault_category || null;

                  // If user interacted with dropdown and value remained same,
                  // treat it as a reselect and open Spare Management.
                  if (
                    faultCategoryDropdownActiveRef.current &&
                    previousValue &&
                    currentValue === previousValue &&
                    onFaultCategorySelect
                  ) {
                    onFaultCategorySelect(currentValue);
                  }

                  faultCategoryDropdownActiveRef.current = false;
                }}
                onChange={e => {
                  const selectedValue = e.target.value || null;
                  onFieldChange('fault_category', selectedValue);
                  faultCategoryDropdownActiveRef.current = false;
                  // Trigger spare parts modal when a fault category is selected
                  if (selectedValue && onFaultCategorySelect) {
                    onFaultCategorySelect(selectedValue);
                  }
                }}
                disabled={loadingCategories}
              >
                <option value="">{loadingCategories ? 'Loading...' : 'Select...'}</option>
                {faultCategories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="filter-label">Capacity Impacted</label>
              <input
                type="number"
                step="0.01"
                className="input"
                placeholder="Enter capacity..."
                value={feedback.capacity_impacted ?? ''}
                onChange={e => {
                  const inputValue = e.target.value;
                  if (inputValue === '') {
                    onFieldChange('capacity_impacted', null);
                  } else {
                    const numValue = parseFloat(inputValue);
                    if (!isNaN(numValue) && isFinite(numValue)) {
                      onFieldChange('capacity_impacted', numValue);
                    }
                  }
                }}
                min="0"
              />
            </div>

            <div className="form-group">
              <label className="filter-label">Assigned At</label>
              <input
                type="datetime-local"
                className="input"
                value={feedback.assigned_at ? new Date(feedback.assigned_at).toISOString().slice(0, 16) : ''}
                onChange={e => onFieldChange('assigned_at', e.target.value ? new Date(e.target.value).toISOString() : null)}
              />
            </div>

            <div className="form-group">
              <label className="filter-label">Alert Source</label>
              <select
                className="input"
                value={feedback.alert_source !== undefined && feedback.alert_source !== null ? feedback.alert_source.toString() : ''}
                onChange={e => onFieldChange('alert_source', e.target.value === '' ? null : parseInt(e.target.value, 10))}
              >
                <option value="">Select...</option>
                <option value="0">Platform Generated</option>
                <option value="1">User Entry</option>
              </select>
            </div>
          </div>

          {/* Grid 4: Selected Spare, Image Upload & Comments */}
          <div className="form-grid-4">
            <div className="form-group">
              <label className="filter-label">Selected Spare</label>
              <input
                className="input"
                placeholder="Select spare from Fault Category..."
                value={feedback.selected_spare || ''}
                onChange={e => onFieldChange('selected_spare', e.target.value || null)}
                readOnly
                style={{ backgroundColor: '#f9fafb', cursor: 'not-allowed' }}
                title="This field is populated from the Spare Management popup"
              />
            </div>

            {/* Image Upload Section */}
            <div className="form-group full-width" style={{ marginTop: '0.5rem' }}>
              <label className="filter-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <ImageIcon size={16} />
                Site Photo / Image
              </label>

              <div className="image-upload-container" style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', marginTop: '8px' }}>
                {/* Upload Button */}
                {!feedback.uploaded_image ? (
                  <label
                    className="upload-box"
                    style={{
                      border: '2px dashed #9ca3af',
                      borderRadius: '8px',
                      padding: '8px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      width: '80px',
                      height: '80px',
                      minWidth: '80px',
                      minHeight: '80px',
                      backgroundColor: '#f9fafb',
                      transition: 'all 0.2s',
                      color: '#4b5563',
                      flexShrink: 0
                    }}
                    title="Upload Photo"
                  >
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      style={{ display: 'none' }}
                    />
                    <ImageIcon size={32} style={{ opacity: 0.7 }} />
                  </label>
                ) : (
                  /* Image Preview Card */
                  <div
                    className="image-preview-card"
                    style={{
                      position: 'relative',
                      width: '80px',
                      height: '80px',
                      minWidth: '80px',
                      minHeight: '80px',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      border: '1px solid #d1d5db',
                      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                      backgroundColor: '#f3f4f6',
                      flexShrink: 0
                    }}
                  >
                    <img
                      src={feedback.uploaded_image}
                      alt="Site condition"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />

                    {/* Overlay Actions */}
                    <div
                      className="preview-overlay"
                      style={{
                        position: 'absolute',
                        inset: 0,
                        backgroundColor: 'rgba(0,0,0,0.4)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        opacity: 0,
                        transition: 'opacity 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '0'}
                    >
                      <button
                        onClick={() => setFullscreenImage(feedback.uploaded_image as string)}
                        type="button"
                        style={{ color: 'white', padding: '6px', borderRadius: '4px', backgroundColor: 'rgba(0,0,0,0.5)' }}
                        title="View Full Size"
                      >
                        <Eye size={18} />
                      </button>
                      <button
                        onClick={handleRemoveImage}
                        type="button"
                        style={{ color: '#ff6b6b', padding: '6px', borderRadius: '4px', backgroundColor: 'rgba(0,0,0,0.5)' }}
                        title="Remove Image"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                )}

                <div className="upload-help-text" style={{ fontSize: '12px', color: '#6b7280', maxWidth: '300px', marginTop: '4px' }}>
                  <p>Upload a photo of the fault or equipment condition.</p>
                  <p style={{ marginTop: '2px' }}>Supports JPG, PNG, WEBP.</p>
                </div>
              </div>
            </div>

            <div className="form-group">
              <label className="filter-label">Comments</label>
              <textarea
                className="input"
                placeholder="Enter comments..."
                value={feedback.comments || ''}
                onChange={e => onFieldChange('comments', e.target.value || null)}
                rows={3}
              />
            </div>
          </div>

          <div className="form-actions">
            <button
              className="btn btn-success"
              onClick={onSave}
              disabled={saving || !feedback.fault_description?.trim()}
            >
              {saving ? <Loader2 className="spinner" /> : <Save />}
              Save
            </button>
            <button
              className="btn btn-secondary"
              onClick={onCancel}
              disabled={saving}
            >
              <X />
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

